import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Button,
  Tabs,
  Tab,
  Badge,
  Avatar,
  Divider,
  FormControlLabel,
  Switch,
  Grid2 as Grid,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Notifications,
  Assignment,
  Build,
  AttachMoney,
  Warning,
  Info,
  CheckCircle,
  Delete,
  MarkEmailRead,
  Settings,
  Email,
  Sms,
  NotificationsActive,
  Schedule,
  Person,
} from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import {
  loadNotifications,
  markNotificationRead,
  loadPpSettings,
  savePpSettings,
} from "../../../../store/actions/ppActions";

const PpNotificationsCenterPage = () => {
  const dispatch = useDispatch();
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const ppState = useSelector((state) => state[currentSession]?.pp);
  const notifications = ppState?.ppNotifications?.data || [];
  const loading = ppState?.ppNotifications?.loading || false;
  const settings = ppState?.settings?.data || {};

  const [activeTab, setActiveTab] = useState(0);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [notificationSettings, setNotificationSettings] = useState({
    emailEnabled: true,
    smsEnabled: true,
    appNotifications: true,
    submissionReminders: true,
    inspectionUpdates: true,
    payoutAlerts: true,
    systemAnnouncements: true,
    reminderTiming: {
      "75%": true,
      "50%": true,
      "25%": true,
      past_due: true,
    },
  });

  useEffect(() => {
    dispatch(loadNotifications());
    dispatch(loadPpSettings());
  }, [dispatch]);

  const handleMarkRead = async (notificationId) => {
    try {
      await dispatch(markNotificationRead(notificationId));
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      const unreadNotifications = notifications.filter((n) => !n.read);
      const promises = unreadNotifications.map((n) =>
        dispatch(markNotificationRead(n.id))
      );
      await Promise.all(promises);
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
    }
  };

  const getNotificationIcon = (type) => {
    const iconMap = {
      submission: <Assignment color="primary" />,
      inspection: <Build color="secondary" />,
      finance: <AttachMoney color="success" />,
      reminder: <Schedule color="warning" />,
      system: <Info color="info" />,
      approval: <CheckCircle color="success" />,
      warning: <Warning color="error" />,
    };

    return iconMap[type] || <Notifications />;
  };

  const getNotificationColor = (type, read) => {
    if (read) return "default";

    const colorMap = {
      submission: "primary",
      inspection: "secondary",
      finance: "success",
      reminder: "warning",
      system: "info",
      approval: "success",
      warning: "error",
    };

    return colorMap[type] || "default";
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now - time) / (1000 * 60));

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;

    return time.toLocaleDateString();
  };

  const filterNotifications = (notifications, filter) => {
    switch (filter) {
      case "unread":
        return notifications.filter((n) => !n.read);
      case "read":
        return notifications.filter((n) => n.read);
      case "important":
        return notifications.filter((n) =>
          ["warning", "reminder"].includes(n.type)
        );
      default:
        return notifications;
    }
  };

  const tabFilters = ["all", "unread", "read", "important"];
  const tabLabels = ["All", "Unread", "Read", "Important"];

  const filteredNotifications = filterNotifications(
    notifications,
    tabFilters[activeTab]
  );
  const unreadCount = notifications.filter((n) => !n.read).length;

  const handleSaveSettings = async () => {
    try {
      const updatedSettings = {
        ...settings,
        notifications: notificationSettings,
      };
      await dispatch(savePpSettings(updatedSettings));
      setSettingsDialogOpen(false);
    } catch (error) {
      console.error("Failed to save notification settings:", error);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography color="primary" variant="h4">
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Badge badgeContent={unreadCount} color="error" max={99}>
              <NotificationsActive color="primary" />
            </Badge>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setSettingsDialogOpen(true)}
            startIcon={<Settings />}
          >
            Settings
          </Button>
          {unreadCount > 0 && (
            <Button
              variant="contained"
              onClick={handleMarkAllRead}
              startIcon={<MarkEmailRead />}
            >
              Mark All Read
            </Button>
          )}
        </Box>
      </Box>

      {/* Notification Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="fullWidth"
        >
          {tabLabels.map((label, index) => (
            <Tab
              key={label}
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {label}
                  {index === 1 && unreadCount > 0 && (
                    <Chip label={unreadCount} size="small" color="error" />
                  )}
                </Box>
              }
            />
          ))}
        </Tabs>
      </Card>

      {/* Notifications List */}
      <Card>
        {loading ? (
          <CardContent>
            <Typography align="center">Loading notifications...</Typography>
          </CardContent>
        ) : filteredNotifications.length === 0 ? (
          <CardContent>
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Notifications sx={{ fontSize: 64, color: "grey.400", mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                {activeTab === 1
                  ? "No unread notifications"
                  : "No notifications found"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {activeTab === 1
                  ? "All caught up! Check back later for new updates."
                  : "Notifications will appear here when there are updates."}
              </Typography>
            </Box>
          </CardContent>
        ) : (
          <List>
            {filteredNotifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <ListItem
                  sx={{
                    bgcolor: notification.read ? "transparent" : "action.hover",
                    "&:hover": { bgcolor: "action.selected" },
                  }}
                >
                  <ListItemIcon>
                    <Avatar
                      sx={{
                        bgcolor: notification.read
                          ? "grey.300"
                          : `${getNotificationColor(notification.type, notification.read)}.main`,
                        width: 40,
                        height: 40,
                      }}
                    >
                      {getNotificationIcon(notification.type)}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography
                          variant="body1"
                          fontWeight={notification.read ? "normal" : "bold"}
                        >
                          {notification.title}
                        </Typography>
                        <Chip
                          label={notification.type}
                          size="small"
                          color={getNotificationColor(
                            notification.type,
                            notification.read
                          )}
                          variant="outlined"
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 0.5 }}
                        >
                          {notification.message}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatTimeAgo(notification.createdAt)}
                        </Typography>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      {!notification.read && (
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleMarkRead(notification.id)}
                          title="Mark as read"
                        >
                          <MarkEmailRead />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        color="error"
                        title="Delete notification"
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
                {index < filteredNotifications.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Card>

      {/* Notification Settings Dialog */}
      <Dialog
        open={settingsDialogOpen}
        onClose={() => setSettingsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Settings />
            Notification Preferences
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Delivery Methods */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" gutterBottom>
                Delivery Methods
              </Typography>
              <Box sx={{ display: "flex", gap: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationSettings.emailEnabled}
                      onChange={(e) =>
                        setNotificationSettings((prev) => ({
                          ...prev,
                          emailEnabled: e.target.checked,
                        }))
                      }
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Email /> Email
                    </Box>
                  }
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationSettings.smsEnabled}
                      onChange={(e) =>
                        setNotificationSettings((prev) => ({
                          ...prev,
                          smsEnabled: e.target.checked,
                        }))
                      }
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Sms /> SMS
                    </Box>
                  }
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationSettings.appNotifications}
                      onChange={(e) =>
                        setNotificationSettings((prev) => ({
                          ...prev,
                          appNotifications: e.target.checked,
                        }))
                      }
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <NotificationsActive /> In-App
                    </Box>
                  }
                />
              </Box>
            </Grid>

            {/* Notification Types */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" gutterBottom>
                Notification Types
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationSettings.submissionReminders}
                      onChange={(e) =>
                        setNotificationSettings((prev) => ({
                          ...prev,
                          submissionReminders: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Submission Due Reminders"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationSettings.inspectionUpdates}
                      onChange={(e) =>
                        setNotificationSettings((prev) => ({
                          ...prev,
                          inspectionUpdates: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Inspection Status Updates"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationSettings.payoutAlerts}
                      onChange={(e) =>
                        setNotificationSettings((prev) => ({
                          ...prev,
                          payoutAlerts: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Payout & Financial Alerts"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationSettings.systemAnnouncements}
                      onChange={(e) =>
                        setNotificationSettings((prev) => ({
                          ...prev,
                          systemAnnouncements: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="System Announcements"
                />
              </Box>
            </Grid>

            {/* Reminder Timing */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" gutterBottom>
                Reminder Timing
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Choose when to receive submission due date reminders:
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                {[
                  { key: "75%", label: "75% (3+ days left)" },
                  { key: "50%", label: "50% (2-3 days left)" },
                  { key: "25%", label: "25% (1 day left)" },
                  { key: "past_due", label: "Past Due" },
                ].map(({ key, label }) => (
                  <FormControlLabel
                    key={key}
                    control={
                      <Switch
                        checked={notificationSettings.reminderTiming[key]}
                        onChange={(e) =>
                          setNotificationSettings((prev) => ({
                            ...prev,
                            reminderTiming: {
                              ...prev.reminderTiming,
                              [key]: e.target.checked,
                            },
                          }))
                        }
                      />
                    }
                    label={label}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveSettings}>
            Save Preferences
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PpNotificationsCenterPage;
